import { useEffect, useRef, useState } from 'react';
import '~/styles/landing.css';
import { useLandingState } from '~/hooks/use-landing-state';
import type { AuthMe } from '~shared/schemas';
import { ParticipantGate } from '~/components/participant-gate';
import { useSyncParticipantLanguage } from '~/hooks/use-participant-language';
import { LandingHeader } from '~/components/landing/header';
import { Hero } from '~/components/landing/hero';
import { Marquee } from '~/components/landing/marquee';
import { Prizes } from '~/components/landing/prizes';
import { HowTo } from '~/components/landing/how-to';
import { InstantLottery } from '~/components/landing/instant-lottery';
import { Mood } from '~/components/landing/mood';
import { Faq } from '~/components/landing/faq';
import { Final } from '~/components/landing/final';
import { Footer } from '~/components/landing/footer';
import { StickyCta } from '~/components/landing/sticky-cta';
import { PrizeModal } from '~/components/landing/modals/prize-modal';
import type { PrizeModalKey } from '~/components/landing/demo-prizes';
import { useSnow } from '~/components/landing/effects/use-snow';
import { useConfetti } from '~/components/landing/effects/use-confetti';
import { useCursorGlow } from '~/components/landing/effects/use-cursor-glow';
import { useReveal } from '~/components/landing/effects/use-reveal';
import { hasFinePointer, prefersReducedMotion } from '~/components/landing/effects/motion-prefs';
import { LANDING_DATES } from '~/components/landing/landing-config';
import { LockReasonContext } from '~/components/landing/locked-badge';
import { notEligibleReason } from '~/components/landing/not-eligible';
import { PromoEnded } from '~/components/landing/promo-ended';

/**
 * Лендинг акции «Новый год 2027» (add-promo-landing, landing-auth-and-locks).
 * Вёрстка и эффекты — `~/components/landing/`, стили — `~/styles/landing.css` (AICODE-NOTE там же).
 *
 * Порядок решений (landing, «Лендинг только для вошедших», «Лендинг после окончания акции»):
 * акция закончилась → «Акция завершена»; иначе — вход участника (`ParticipantGate`) и лендинг.
 */
export function LandingPage() {
  const ended = usePromoEnded();
  return ended ? <PromoEnded /> : <LandingGate />;
}

/** setTimeout не держит задержку больше ~24,8 суток — дальний конец акции перепроверяется по частям. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1;
const LANDING_END_MS = Date.parse(LANDING_DATES.landingEnd);

function usePromoEnded(): boolean {
  const [now, setNow] = useState(() => Date.now());
  const ended = now >= LANDING_END_MS;
  useEffect(() => {
    if (ended) return;
    const id = setTimeout(() => setNow(Date.now()), Math.min(LANDING_END_MS - now, MAX_TIMEOUT_MS));
    return () => clearTimeout(id);
  }, [now, ended]);
  return ended;
}

function LandingGate() {
  return <ParticipantGate>{(participant) => <LandingContent participant={participant} />}</ParticipantGate>;
}

function LandingContent({ participant }: { participant: AuthMe }) {
  useSyncParticipantLanguage();
  const landingState = useLandingState();

  const rootRef = useRef<HTMLDivElement>(null);
  const snowCanvasRef = useRef<HTMLCanvasElement>(null);
  const fxCanvasRef = useRef<HTMLCanvasElement>(null);
  const cglowRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const giftsRef = useRef<HTMLDivElement>(null);
  const finalRef = useRef<HTMLDivElement>(null);

  const snow = useSnow(snowCanvasRef);
  const burst = useConfetti(fxCanvasRef);
  useCursorGlow(cglowRef);
  useReveal(rootRef);

  const [openPrize, setOpenPrize] = useState<PrizeModalKey | null>(null);

  // Полоса прогресса прокрутки + тень шапки (js/main.js, «header / progress») — не мотивное
  // decor-эффект, не завязан на prefers-reduced-motion (как и в макете).
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const h = document.documentElement.scrollHeight - window.innerHeight;
      if (progressRef.current) progressRef.current.style.width = `${h > 0 ? (y / h) * 100 : 0}%`;
      setScrolled(y > 10);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Не участвует в акции — замочки навсегда, даже при подтверждении до правила (accrue-only-promo-countries).
  const notEligible = notEligibleReason(participant);
  const confirmed = landingState.data?.participationConfirmed ?? false;
  const balance = notEligible ? null : (landingState.data?.balance ?? null);
  const goatsTotal = notEligible ? null : (landingState.data?.goatsTotal ?? null);
  // Решение о замочках принимается здесь одно на все блоки (landing-auth-and-locks, design.md, решение 2).
  const locked = !confirmed || notEligible !== null;

  return (
    <LockReasonContext.Provider value={notEligible ? 'notEligible' : 'notConfirmed'}>
      <div className="ny-landing" ref={rootRef}>
        <div id="progress" ref={progressRef} />
        <canvas id="snow" ref={snowCanvasRef} />
        <canvas id="fx" ref={fxCanvasRef} />
        {hasFinePointer() && !prefersReducedMotion() && <div className="cglow" id="cglow" ref={cglowRef} />}

        <LandingHeader
          snowOn={snow.on}
          onToggleSnow={snow.toggle}
          balance={balance}
          participationConfirmed={confirmed}
          notEligible={notEligible}
          scrolled={scrolled}
          participant={participant}
        />

        <main id="top">
          <div ref={heroRef}>
            <Hero balance={balance} participationConfirmed={confirmed} notEligible={notEligible} />
          </div>
          <Marquee />
          <Prizes locked={locked} balance={balance} goatsTotal={goatsTotal} onOpenPrizeModal={setOpenPrize} />
          <HowTo />
          <div ref={giftsRef}>
            <InstantLottery locked={locked} balance={balance} burst={burst} />
          </div>
          <Mood />
          <Faq />
          <div ref={finalRef}>
            <Final />
          </div>
        </main>
        <Footer />

        <StickyCta heroRef={heroRef} giftsRef={giftsRef} finalRef={finalRef} balance={balance} participationConfirmed={confirmed} notEligible={notEligible} />

        {openPrize && <PrizeModal prizeKey={openPrize} onClose={() => setOpenPrize(null)} />}
      </div>
    </LockReasonContext.Provider>
  );
}
